import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  MenuItem,
  Button,
  Menu,
  CircularProgress,
  Alert,
  TextField,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PaymentIcon from "@mui/icons-material/Payment";
import StarIcon from "@mui/icons-material/Star";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AppleIcon from "@mui/icons-material/Apple";
import axios from "axios";
import { getFullApiUrl } from "../../config";
import { getToken } from "../../utils";
import { ExpiredPage } from "../ExpiredPage";
import { LoadingPage } from "../LoadingPage";
import { ErrorPage } from "../ErrorPage";

const UserManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);

  // State for per-user Stripe official data (fetched on-demand)
  const [userStripeData, setUserStripeData] = useState({}); // { [email]: { loading, error, data } }

  // State for Stripe revenue data
  const [stripeRevenue, setStripeRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [revenueError, setRevenueError] = useState(null);

  // Filter states
  const [verifiedFilter, setVerifiedFilter] = useState("all");
  const [zsxqFilter, setZsxqFilter] = useState("all");
  const [stripeFilter, setStripeFilter] = useState("all");
  const [appleFilter, setAppleFilter] = useState("all");

  // Menu anchor states
  const [verifiedAnchor, setVerifiedAnchor] = useState(null);
  const [zsxqAnchor, setZsxqAnchor] = useState(null);
  const [stripeAnchor, setStripeAnchor] = useState(null);
  const [appleAnchor, setAppleAnchor] = useState(null);

  // Fix Stripe payment states
  const [fixingUser, setFixingUser] = useState(null); // email of user being fixed
  const [fixResult, setFixResult] = useState(null); // { email, success, message, purchases_created }

  // Manual membership states
  const [manualMembershipForm, setManualMembershipForm] = useState({}); // { [userId]: { endDate, reason } }
  const [manualMembershipLoading, setManualMembershipLoading] = useState({}); // { [userId]: true/false }
  const [manualMembershipResult, setManualMembershipResult] = useState({}); // { [userId]: { success, message } }

  // Apple subscription refresh states
  const [refreshingApple, setRefreshingApple] = useState(null); // originalTransactionID being refreshed
  const [refreshAppleResult, setRefreshAppleResult] = useState(null); // { originalTransactionID, success, message }

  useEffect(() => {
    const fetchUsersData = async () => {
      try {
        const token = getToken();
        setLoading(true);

        const response = await axios.get(getFullApiUrl("/api/v1/admin/users"), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUsers(response.data.users);
      } catch (err) {
        if (err.response?.data?.error === "token has expired") {
          setExpired(true);
        } else {
          console.error("Error fetching users data:", err);
          setError("Failed to load users data. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchUsersData();
  }, []);

  useEffect(() => {
    const fetchStripeRevenue = async () => {
      try {
        const token = getToken();
        setRevenueLoading(true);
        setRevenueError(null);

        const response = await axios.get(
          getFullApiUrl("/api/v1/admin/stripe-revenue"),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setStripeRevenue(response.data);
      } catch (err) {
        if (err.response?.data?.error === "token has expired") {
          setExpired(true);
        } else {
          console.error("Error fetching Stripe revenue data:", err);
          setRevenueError(
            "Failed to load Stripe revenue data. Please try again later."
          );
        }
      } finally {
        setRevenueLoading(false);
      }
    };
    fetchStripeRevenue();
  }, []);

  // Function to fix missing Stripe payment data
  const handleFixStripePayment = async (email, stripeCustomerId) => {
    try {
      setFixingUser(email);
      setFixResult(null);
      const token = getToken();

      const response = await axios.post(
        getFullApiUrl("/api/v1/admin/fix-stripe-payment"),
        {
          email: email,
          stripe_customer_id: stripeCustomerId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setFixResult({
        email,
        success: true,
        message: response.data.message,
        purchases_created: response.data.purchases_created,
      });

      // Refresh user data to reflect the changes
      const usersResponse = await axios.get(getFullApiUrl("/api/v1/admin/users"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(usersResponse.data.users);
    } catch (err) {
      console.error("Error fixing Stripe payment:", err);
      setFixResult({
        email,
        success: false,
        message: err.response?.data?.error || "修复失败，请重试",
      });
    } finally {
      setFixingUser(null);
    }
  };

  // Helper to get default end date (1 year from now)
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split("T")[0];
  };

  // Handle manual membership form change
  const handleManualMembershipFormChange = (userId, field, value) => {
    setManualMembershipForm((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value,
      },
    }));
  };

  // Create manual membership
  const handleCreateManualMembership = async (userId) => {
    const form = manualMembershipForm[userId] || {};
    const endDate = form.endDate || getDefaultEndDate();
    const reason = form.reason || "";

    try {
      setManualMembershipLoading((prev) => ({ ...prev, [userId]: true }));
      setManualMembershipResult((prev) => ({ ...prev, [userId]: null }));
      const token = getToken();

      await axios.post(
        getFullApiUrl("/api/v1/admin/manual-membership"),
        {
          user_id: userId,
          membership_end_at: new Date(endDate).toISOString(),
          reason: reason,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setManualMembershipResult((prev) => ({
        ...prev,
        [userId]: { success: true, message: "手动会员创建成功" },
      }));

      // Refresh user data
      const usersResponse = await axios.get(getFullApiUrl("/api/v1/admin/users"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(usersResponse.data.users);
    } catch (err) {
      setManualMembershipResult((prev) => ({
        ...prev,
        [userId]: {
          success: false,
          message: err.response?.data?.error || "创建失败，请重试",
        },
      }));
    } finally {
      setManualMembershipLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // Update manual membership
  const handleUpdateManualMembership = async (membershipId, userId) => {
    const form = manualMembershipForm[userId] || {};

    try {
      setManualMembershipLoading((prev) => ({ ...prev, [userId]: true }));
      setManualMembershipResult((prev) => ({ ...prev, [userId]: null }));
      const token = getToken();

      const updateData = {};
      if (form.endDate) {
        updateData.membership_end_at = new Date(form.endDate).toISOString();
      }
      if (form.reason !== undefined) {
        updateData.reason = form.reason;
      }

      await axios.put(
        getFullApiUrl(`/api/v1/admin/manual-membership/${membershipId}`),
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setManualMembershipResult((prev) => ({
        ...prev,
        [userId]: { success: true, message: "更新成功" },
      }));

      // Refresh user data
      const usersResponse = await axios.get(getFullApiUrl("/api/v1/admin/users"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(usersResponse.data.users);
    } catch (err) {
      setManualMembershipResult((prev) => ({
        ...prev,
        [userId]: {
          success: false,
          message: err.response?.data?.error || "更新失败，请重试",
        },
      }));
    } finally {
      setManualMembershipLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // Delete manual membership
  const handleDeleteManualMembership = async (membershipId, userId) => {
    if (!window.confirm("确定要删除此手动会员吗？用户将立即失去会员权限。")) {
      return;
    }

    try {
      setManualMembershipLoading((prev) => ({ ...prev, [userId]: true }));
      setManualMembershipResult((prev) => ({ ...prev, [userId]: null }));
      const token = getToken();

      await axios.delete(
        getFullApiUrl(`/api/v1/admin/manual-membership/${membershipId}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setManualMembershipResult((prev) => ({
        ...prev,
        [userId]: { success: true, message: "已删除手动会员" },
      }));

      // Refresh user data
      const usersResponse = await axios.get(getFullApiUrl("/api/v1/admin/users"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(usersResponse.data.users);
    } catch (err) {
      setManualMembershipResult((prev) => ({
        ...prev,
        [userId]: {
          success: false,
          message: err.response?.data?.error || "删除失败，请重试",
        },
      }));
    } finally {
      setManualMembershipLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // Refresh Apple subscription status from Apple API
  const handleRefreshAppleSubscription = async (originalTransactionID) => {
    try {
      setRefreshingApple(originalTransactionID);
      setRefreshAppleResult(null);
      const token = getToken();

      const response = await axios.post(
        getFullApiUrl(`/api/v1/admin/apple/refresh-subscription/${originalTransactionID}`),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setRefreshAppleResult({
        originalTransactionID,
        success: true,
        message: `状态已更新为: ${response.data.subscription?.status || "未知"}`,
      });

      // Refresh user data to reflect the changes
      const usersResponse = await axios.get(getFullApiUrl("/api/v1/admin/users"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(usersResponse.data.users);
    } catch (err) {
      console.error("Error refreshing Apple subscription:", err);
      setRefreshAppleResult({
        originalTransactionID,
        success: false,
        message: err.response?.data?.error || "刷新失败，请重试",
      });
    } finally {
      setRefreshingApple(null);
    }
  };

  // Fetch Stripe data for a specific user on-demand
  const fetchUserStripeData = async (email) => {
    if (userStripeData[email]?.data || userStripeData[email]?.loading) return;

    setUserStripeData((prev) => ({
      ...prev,
      [email]: { loading: true, error: null, data: null },
    }));

    try {
      const token = getToken();
      const response = await axios.get(
        getFullApiUrl(`/api/v1/admin/stripe-payments/${encodeURIComponent(email)}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUserStripeData((prev) => ({
        ...prev,
        [email]: { loading: false, error: null, data: response.data },
      }));
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        setExpired(true);
      } else {
        setUserStripeData((prev) => ({
          ...prev,
          [email]: {
            loading: false,
            error: err.response?.data?.error || "加载失败",
            data: null,
          },
        }));
      }
    }
  };

  // Check if user needs Stripe fix (has official Stripe record but no DB purchases)
  const userNeedsStripeFix = (user) => {
    const stripeData = userStripeData[user.email]?.data;
    if (!stripeData) return false;
    const hasOfficialStripeRecord = stripeData.records?.length > 0 && stripeData.customer_id;
    const hasDbPurchases = user.stripe_purchases && user.stripe_purchases.length > 0;
    const hasDbSubscriptions = user.stripe_subscriptions && user.stripe_subscriptions.length > 0;
    return hasOfficialStripeRecord && !hasDbPurchases && !hasDbSubscriptions;
  };

  // Get Stripe customer ID for a user from fetched data
  const getStripeCustomerId = (user) => {
    return userStripeData[user.email]?.data?.customer_id;
  };

  // Filter users based on selected filters
  const filteredUsers = users.filter((user) => {
    // Verified filter
    if (verifiedFilter === "verified" && !user.verified) return false;
    if (verifiedFilter === "unverified" && user.verified) return false;

    // ZSXQ filter
    if (
      zsxqFilter === "has_subscription" &&
      (!user.zsxq_subscriptions || user.zsxq_subscriptions.length === 0)
    )
      return false;
    if (
      zsxqFilter === "no_subscription" &&
      user.zsxq_subscriptions &&
      user.zsxq_subscriptions.length > 0
    )
      return false;
    if (
      zsxqFilter === "active" &&
      (!user.zsxq_subscriptions ||
        !user.zsxq_subscriptions.some((sub) => sub.is_active))
    )
      return false;
    if (
      zsxqFilter === "expired" &&
      (!user.zsxq_subscriptions ||
        user.zsxq_subscriptions.length === 0 ||
        user.zsxq_subscriptions.some((sub) => sub.is_active))
    )
      return false;

    // Stripe filter
    const hasStripeSubscription =
      user.stripe_subscriptions && user.stripe_subscriptions.length > 0;
    const hasStripePurchase =
      user.stripe_purchases && user.stripe_purchases.length > 0;
    const hasActiveStripe =
      (user.stripe_subscriptions &&
        user.stripe_subscriptions.some((sub) => sub.is_active)) ||
      (user.stripe_purchases &&
        user.stripe_purchases.some((purchase) => purchase.is_active));

    if (
      stripeFilter === "has_payment" &&
      !hasStripeSubscription &&
      !hasStripePurchase
    )
      return false;
    if (
      stripeFilter === "no_payment" &&
      (hasStripeSubscription || hasStripePurchase)
    )
      return false;
    if (stripeFilter === "active" && !hasActiveStripe) return false;
    if (
      stripeFilter === "expired" &&
      ((!hasStripeSubscription && !hasStripePurchase) || hasActiveStripe)
    )
      return false;

    // Apple filter
    const hasAppleSubscription =
      user.apple_subscriptions && user.apple_subscriptions.length > 0;
    const hasActiveApple =
      user.apple_subscriptions &&
      user.apple_subscriptions.some((sub) => sub.is_active);

    if (appleFilter === "has_subscription" && !hasAppleSubscription)
      return false;
    if (appleFilter === "no_subscription" && hasAppleSubscription)
      return false;
    if (appleFilter === "active" && !hasActiveApple) return false;
    if (
      appleFilter === "expired" &&
      (!hasAppleSubscription || hasActiveApple)
    )
      return false;

    return true;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const handleFilterClick = (event, filterType) => {
    switch (filterType) {
      case "verified":
        setVerifiedAnchor(event.currentTarget);
        break;
      case "zsxq":
        setZsxqAnchor(event.currentTarget);
        break;
      case "stripe":
        setStripeAnchor(event.currentTarget);
        break;
      case "apple":
        setAppleAnchor(event.currentTarget);
        break;
      default:
        break;
    }
  };

  const handleFilterClose = (filterType) => {
    switch (filterType) {
      case "verified":
        setVerifiedAnchor(null);
        break;
      case "zsxq":
        setZsxqAnchor(null);
        break;
      case "stripe":
        setStripeAnchor(null);
        break;
      case "apple":
        setAppleAnchor(null);
        break;
      default:
        break;
    }
  };

  const handleFilterSelect = (filterType, value) => {
    switch (filterType) {
      case "verified":
        setVerifiedFilter(value);
        setVerifiedAnchor(null);
        break;
      case "zsxq":
        setZsxqFilter(value);
        setZsxqAnchor(null);
        break;
      case "stripe":
        setStripeFilter(value);
        setStripeAnchor(null);
        break;
      case "apple":
        setAppleFilter(value);
        setAppleAnchor(null);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (expired) {
    return <ExpiredPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("zh-CN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (isActive) => {
    return isActive ? "success" : "default";
  };

  const getStatusText = (isActive) => {
    return isActive ? "未过期" : "已过期";
  };

  return (
    <Container component="main" maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          用户管理
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          查看所有用户的邮箱、知识星球和Stripe付费订阅情况
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 4 }}>
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6">总用户数</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {filteredUsers.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <StarIcon sx={{ mr: 1 }} />
              <Typography variant="h6">知识星球订阅</Typography>
            </Box>
            <Typography variant="h4" color="warning.main">
              {
                filteredUsers.filter((user) =>
                  user.zsxq_subscriptions?.some((sub) => sub.is_active)
                ).length
              }
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <PaymentIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Stripe付费用户</Typography>
            </Box>
            <Typography variant="h4" color="success.main">
              {
                filteredUsers.filter(
                  (user) =>
                    user.stripe_subscriptions?.some((sub) => sub.is_active) ||
                    user.stripe_purchases?.some(
                      (purchase) => purchase.is_active
                    )
                ).length
              }
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <AppleIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Apple订阅用户</Typography>
            </Box>
            <Typography variant="h4" color="info.main">
              {
                filteredUsers.filter((user) =>
                  user.apple_subscriptions?.some((sub) => sub.is_active)
                ).length
              }
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <CardGiftcardIcon sx={{ mr: 1 }} />
              <Typography variant="h6">手动会员</Typography>
            </Box>
            <Typography variant="h4" color="secondary.main">
              {
                filteredUsers.filter((user) =>
                  user.manual_membership?.is_active
                ).length
              }
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <AttachMoneyIcon sx={{ mr: 1, color: "warning.main" }} />
              <Typography variant="h6">Stripe总收入</Typography>
            </Box>
            {revenueLoading ? (
              <Typography variant="h4" color="text.secondary">
                加载中...
              </Typography>
            ) : revenueError ? (
              <Typography variant="body2" color="error">
                加载失败
              </Typography>
            ) : (
              <Typography variant="h4" color="warning.main">
                ¥
                {stripeRevenue?.total_revenue?.cny
                  ? (stripeRevenue.total_revenue.cny / 100).toFixed(2)
                  : "0.00"}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>用户邮箱</TableCell>
              <TableCell>注册时间</TableCell>
              <TableCell>
                <Button
                  onClick={(e) => handleFilterClick(e, "verified")}
                  endIcon={<FilterListIcon />}
                  sx={{
                    color: "inherit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "primary.main",
                    },
                  }}
                >
                  已验证
                </Button>
                <Menu
                  anchorEl={verifiedAnchor}
                  open={Boolean(verifiedAnchor)}
                  onClose={() => handleFilterClose("verified")}
                >
                  <MenuItem
                    onClick={() => handleFilterSelect("verified", "all")}
                  >
                    全部
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("verified", "verified")}
                  >
                    已验证
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("verified", "unverified")}
                  >
                    未验证
                  </MenuItem>
                </Menu>
              </TableCell>
              <TableCell>
                <Button
                  onClick={(e) => handleFilterClick(e, "zsxq")}
                  endIcon={<FilterListIcon />}
                  sx={{
                    color: "inherit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "primary.main",
                    },
                  }}
                >
                  知识星球
                </Button>
                <Menu
                  anchorEl={zsxqAnchor}
                  open={Boolean(zsxqAnchor)}
                  onClose={() => handleFilterClose("zsxq")}
                >
                  <MenuItem onClick={() => handleFilterSelect("zsxq", "all")}>
                    全部
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleFilterSelect("zsxq", "has_subscription")
                    }
                  >
                    有订阅
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleFilterSelect("zsxq", "no_subscription")
                    }
                  >
                    无订阅
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("zsxq", "active")}
                  >
                    未过期订阅
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("zsxq", "expired")}
                  >
                    已过期订阅
                  </MenuItem>
                </Menu>
              </TableCell>
              <TableCell>
                <Button
                  onClick={(e) => handleFilterClick(e, "stripe")}
                  endIcon={<FilterListIcon />}
                  sx={{
                    color: "inherit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "primary.main",
                    },
                  }}
                >
                  Stripe订阅
                </Button>
                <Menu
                  anchorEl={stripeAnchor}
                  open={Boolean(stripeAnchor)}
                  onClose={() => handleFilterClose("stripe")}
                >
                  <MenuItem onClick={() => handleFilterSelect("stripe", "all")}>
                    全部
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("stripe", "has_payment")}
                  >
                    有付费记录
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("stripe", "no_payment")}
                  >
                    无付费记录
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("stripe", "active")}
                  >
                    购买：未过期
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("stripe", "expired")}
                  >
                    购买：已过期
                  </MenuItem>
                </Menu>
              </TableCell>
              <TableCell>
                <Button
                  onClick={(e) => handleFilterClick(e, "apple")}
                  endIcon={<FilterListIcon />}
                  sx={{
                    color: "inherit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    p: 0,
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "primary.main",
                    },
                  }}
                >
                  Apple订阅
                </Button>
                <Menu
                  anchorEl={appleAnchor}
                  open={Boolean(appleAnchor)}
                  onClose={() => handleFilterClose("apple")}
                >
                  <MenuItem onClick={() => handleFilterSelect("apple", "all")}>
                    全部
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("apple", "has_subscription")}
                  >
                    有订阅
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("apple", "no_subscription")}
                  >
                    无订阅
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("apple", "active")}
                  >
                    订阅：未过期
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleFilterSelect("apple", "expired")}
                  >
                    订阅：已过期
                  </MenuItem>
                </Menu>
              </TableCell>
              <TableCell>手动会员</TableCell>
              <TableCell>详细信息</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <EmailIcon sx={{ mr: 1, color: "action.active" }} />
                    {user.email}
                  </Box>
                </TableCell>
                <TableCell>{formatDate(user.created_at)}</TableCell>
                <TableCell>
                  <Chip
                    label={user.verified ? "是" : "否"}
                    color={user.verified ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.zsxq_subscriptions?.length > 0 ? (
                    <Box>
                      {user.zsxq_subscriptions.map((sub, index) => (
                        <Chip
                          key={index}
                          label={getStatusText(sub.is_active)}
                          color={getStatusColor(sub.is_active)}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  ) : (
                    "无"
                  )}
                </TableCell>
                <TableCell>
                  {user.stripe_subscriptions?.length > 0 ||
                  user.stripe_purchases?.length > 0 ? (
                    <Box>
                      {user.stripe_subscriptions?.map((sub, index) => (
                        <Chip
                          key={`sub-${index}`}
                          label={`订阅: ${getStatusText(sub.is_active)}`}
                          color={getStatusColor(sub.is_active)}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                      {user.stripe_purchases?.map((purchase, index) => (
                        <Chip
                          key={`purchase-${index}`}
                          label={`购买: ${getStatusText(purchase.is_active)}`}
                          color={getStatusColor(purchase.is_active)}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  ) : (
                    "无"
                  )}
                </TableCell>
                <TableCell>
                  {user.apple_subscriptions?.length > 0 ? (
                    <Box>
                      {user.apple_subscriptions.map((sub, index) => (
                        <Chip
                          key={index}
                          label={getStatusText(sub.is_active)}
                          color={getStatusColor(sub.is_active)}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  ) : (
                    "无"
                  )}
                </TableCell>
                <TableCell>
                  {user.manual_membership ? (
                    <Chip
                      label={getStatusText(user.manual_membership.is_active)}
                      color={getStatusColor(user.manual_membership.is_active)}
                      size="small"
                    />
                  ) : (
                    "无"
                  )}
                </TableCell>
                <TableCell>
                  <Accordion
                    onChange={(event, isExpanded) => {
                      if (isExpanded) {
                        fetchUserStripeData(user.email);
                      }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2">查看详情</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {/* 知识星球详情 */}
                        {user.zsxq_subscriptions?.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              知识星球订阅
                            </Typography>
                            {user.zsxq_subscriptions.map((sub, index) => (
                              <Paper key={index} sx={{ p: 2, mb: 1 }}>
                                <Typography variant="body2">
                                  <strong>名称:</strong> {sub.name}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>编号:</strong> {sub.number}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>到期时间:</strong>{" "}
                                  {formatDate(sub.membership_end_at)}
                                </Typography>
                                <Chip
                                  label={getStatusText(sub.is_active)}
                                  color={getStatusColor(sub.is_active)}
                                  size="small"
                                />
                              </Paper>
                            ))}
                          </Box>
                        )}

                        {/* Stripe订阅详情 */}
                        {user.stripe_subscriptions?.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Stripe订阅
                            </Typography>
                            {user.stripe_subscriptions.map((sub, index) => (
                              <Paper key={index} sx={{ p: 2, mb: 1 }}>
                                <Typography variant="body2">
                                  <strong>状态:</strong> {sub.status}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>当前周期:</strong>{" "}
                                  {formatDate(sub.current_period_start)} -{" "}
                                  {formatDate(sub.current_period_end)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>周期结束时取消:</strong>{" "}
                                  {sub.cancel_at_period_end ? "是" : "否"}
                                </Typography>
                                <Chip
                                  label={getStatusText(sub.is_active)}
                                  color={getStatusColor(sub.is_active)}
                                  size="small"
                                />
                              </Paper>
                            ))}
                          </Box>
                        )}

                        {/* Stripe购买详情 */}
                        {user.stripe_purchases?.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Stripe一次性购买
                            </Typography>
                            {user.stripe_purchases.map((purchase, index) => (
                              <Paper key={index} sx={{ p: 2, mb: 1 }}>
                                <Typography variant="body2">
                                  <strong>金额:</strong>{" "}
                                  {formatCurrency(
                                    purchase.amount,
                                    purchase.currency
                                  )}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>状态:</strong> {purchase.status}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>购买时间:</strong>{" "}
                                  {formatDate(purchase.purchased_at)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>到期时间:</strong>{" "}
                                  {formatDate(purchase.expires_at)}
                                </Typography>
                                <Chip
                                  label={getStatusText(purchase.is_active)}
                                  color={getStatusColor(purchase.is_active)}
                                  size="small"
                                />
                              </Paper>
                            ))}
                          </Box>
                        )}

                        {/* Apple订阅详情 */}
                        {user.apple_subscriptions?.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                              <AppleIcon sx={{ mr: 1, fontSize: 18 }} />
                              Apple订阅
                            </Typography>
                            {user.apple_subscriptions.map((sub, index) => (
                              <Paper key={index} sx={{ p: 2, mb: 1 }}>
                                <Typography variant="body2">
                                  <strong>产品ID:</strong> {sub.product_id}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>状态:</strong> {sub.status}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>环境:</strong>{" "}
                                  <Chip
                                    label={sub.environment === "Production" ? "生产" : "沙盒"}
                                    color={sub.environment === "Production" ? "primary" : "warning"}
                                    size="small"
                                  />
                                </Typography>
                                <Typography variant="body2">
                                  <strong>首次购买时间:</strong>{" "}
                                  {formatDate(sub.original_purchase_date)}
                                </Typography>
                                {sub.expires_date && (
                                  <Typography variant="body2">
                                    <strong>到期时间:</strong>{" "}
                                    {formatDate(sub.expires_date)}
                                  </Typography>
                                )}
                                <Typography variant="body2">
                                  <strong>自动续订:</strong>{" "}
                                  {sub.auto_renew_status ? "是" : "否"}
                                </Typography>
                                {sub.is_in_billing_retry && (
                                  <Typography variant="body2" color="warning.main">
                                    <strong>计费重试中:</strong> 是
                                  </Typography>
                                )}
                                {sub.grace_period_expires_date && (
                                  <Typography variant="body2">
                                    <strong>宽限期到期:</strong>{" "}
                                    {formatDate(sub.grace_period_expires_date)}
                                  </Typography>
                                )}
                                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                  <Chip
                                    label={getStatusText(sub.is_active)}
                                    color={getStatusColor(sub.is_active)}
                                    size="small"
                                  />
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={refreshingApple === sub.original_transaction_id}
                                    onClick={() => handleRefreshAppleSubscription(sub.original_transaction_id)}
                                    startIcon={
                                      refreshingApple === sub.original_transaction_id ? (
                                        <CircularProgress size={16} color="inherit" />
                                      ) : null
                                    }
                                  >
                                    {refreshingApple === sub.original_transaction_id
                                      ? "刷新中..."
                                      : "刷新状态"}
                                  </Button>
                                </Box>
                                {/* Show refresh result */}
                                {refreshAppleResult && refreshAppleResult.originalTransactionID === sub.original_transaction_id && (
                                  <Alert
                                    severity={refreshAppleResult.success ? "success" : "error"}
                                    sx={{ mt: 1 }}
                                  >
                                    {refreshAppleResult.message}
                                  </Alert>
                                )}
                              </Paper>
                            ))}
                          </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* 手动会员管理 */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                            <CardGiftcardIcon sx={{ mr: 1, fontSize: 18 }} />
                            手动会员管理
                          </Typography>

                          {/* 显示现有手动会员 */}
                          {user.manual_membership && (
                            <Paper sx={{ p: 2, mb: 2, bgcolor: user.manual_membership.is_active ? "success.light" : "grey.100" }}>
                              <Typography variant="body2">
                                <strong>状态:</strong>{" "}
                                <Chip
                                  label={getStatusText(user.manual_membership.is_active)}
                                  color={getStatusColor(user.manual_membership.is_active)}
                                  size="small"
                                />
                              </Typography>
                              <Typography variant="body2">
                                <strong>到期时间:</strong>{" "}
                                {formatDate(user.manual_membership.membership_end_at)}
                              </Typography>
                              {user.manual_membership.reason && (
                                <Typography variant="body2">
                                  <strong>原因:</strong> {user.manual_membership.reason}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.secondary">
                                <strong>创建时间:</strong>{" "}
                                {formatDate(user.manual_membership.created_at)}
                              </Typography>
                            </Paper>
                          )}

                          {/* 表单：创建或更新手动会员 */}
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
                            <TextField
                              label="到期时间"
                              type="date"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              value={
                                manualMembershipForm[user.id]?.endDate ||
                                (user.manual_membership
                                  ? new Date(user.manual_membership.membership_end_at)
                                      .toISOString()
                                      .split("T")[0]
                                  : getDefaultEndDate())
                              }
                              onChange={(e) =>
                                handleManualMembershipFormChange(
                                  user.id,
                                  "endDate",
                                  e.target.value
                                )
                              }
                            />
                            <TextField
                              label="原因 (可选)"
                              size="small"
                              placeholder="例如：VIP用户、特殊优惠"
                              value={
                                manualMembershipForm[user.id]?.reason ??
                                (user.manual_membership?.reason || "")
                              }
                              onChange={(e) =>
                                handleManualMembershipFormChange(
                                  user.id,
                                  "reason",
                                  e.target.value
                                )
                              }
                            />
                          </Box>

                          {/* 操作按钮 */}
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {!user.manual_membership ? (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={manualMembershipLoading[user.id]}
                                onClick={() => handleCreateManualMembership(user.id)}
                                startIcon={
                                  manualMembershipLoading[user.id] ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : null
                                }
                              >
                                {manualMembershipLoading[user.id]
                                  ? "创建中..."
                                  : "创建手动会员"}
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  disabled={manualMembershipLoading[user.id]}
                                  onClick={() =>
                                    handleUpdateManualMembership(
                                      user.manual_membership.id,
                                      user.id
                                    )
                                  }
                                  startIcon={
                                    manualMembershipLoading[user.id] ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : null
                                  }
                                >
                                  {manualMembershipLoading[user.id]
                                    ? "更新中..."
                                    : "更新到期时间"}
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  disabled={manualMembershipLoading[user.id]}
                                  onClick={() =>
                                    handleDeleteManualMembership(
                                      user.manual_membership.id,
                                      user.id
                                    )
                                  }
                                >
                                  删除手动会员
                                </Button>
                              </>
                            )}
                          </Box>

                          {/* 显示操作结果 */}
                          {manualMembershipResult[user.id] && (
                            <Alert
                              severity={
                                manualMembershipResult[user.id].success
                                  ? "success"
                                  : "error"
                              }
                              sx={{ mt: 1 }}
                            >
                              {manualMembershipResult[user.id].message}
                            </Alert>
                          )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Stripe官方数据 */}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Stripe官方数据
                          </Typography>
                          {userStripeData[user.email]?.loading ? (
                            <Typography variant="body2" color="text.secondary">
                              正在从Stripe官方API加载数据...
                            </Typography>
                          ) : userStripeData[user.email]?.error ? (
                            <Typography variant="body2" color="error">
                              加载Stripe官方数据失败: {userStripeData[user.email].error}
                            </Typography>
                          ) : userStripeData[user.email]?.data ? (
                            <>
                              {userStripeData[user.email].data.customer_id && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>客户ID:</strong>{" "}
                                  {userStripeData[user.email].data.customer_id}
                                </Typography>
                              )}
                              {userStripeData[user.email].data.records?.map((record, index) => (
                                <Paper key={index} sx={{ p: 2, mb: 1 }}>
                                  {record.customer_id && (
                                    <Typography variant="body2">
                                      <strong>客户ID:</strong>{" "}
                                      {record.customer_id}
                                    </Typography>
                                  )}
                                  <Typography variant="body2">
                                    <strong>来源:</strong>{" "}
                                    {record.source === "subscription"
                                      ? "订阅"
                                      : record.source === "charge"
                                      ? "一次性付款"
                                      : record.source === "charge_no_customer"
                                      ? "一次性付款(无客户)"
                                      : "客户数据"}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>状态:</strong> {record.status}
                                  </Typography>
                                  {record.amount && record.currency && (
                                    <Typography variant="body2">
                                      <strong>金额:</strong>{" "}
                                      {formatCurrency(
                                        record.amount,
                                        record.currency
                                      )}
                                    </Typography>
                                  )}
                                  {record.refunded && (
                                    <Typography variant="body2" color="error">
                                      <strong>退款状态:</strong> 已退款
                                    </Typography>
                                  )}
                                  {record.amount_refunded > 0 && (
                                    <Typography variant="body2" color="error">
                                      <strong>退款金额:</strong>{" "}
                                      {formatCurrency(
                                        record.amount_refunded,
                                        record.currency
                                      )}
                                    </Typography>
                                  )}
                                  <Typography variant="body2">
                                    <strong>创建时间:</strong>{" "}
                                    {formatDate(record.created)}
                                  </Typography>
                                </Paper>
                              ))}
                              {(!userStripeData[user.email].data.records ||
                                userStripeData[user.email].data.records.length === 0) && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  该用户未在Stripe官方付费记录中找到
                                </Typography>
                              )}
                              {/* Fix button for users with Stripe record but no DB record */}
                              {userNeedsStripeFix(user) && (
                                <Box sx={{ mt: 2 }}>
                                  <Button
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    disabled={fixingUser === user.email}
                                    onClick={() =>
                                      handleFixStripePayment(
                                        user.email,
                                        getStripeCustomerId(user)
                                      )
                                    }
                                    startIcon={
                                      fixingUser === user.email ? (
                                        <CircularProgress size={16} color="inherit" />
                                      ) : null
                                    }
                                  >
                                    {fixingUser === user.email
                                      ? "正在修复..."
                                      : "修复Stripe数据到数据库"}
                                  </Button>
                                </Box>
                              )}
                              {/* Show fix result */}
                              {fixResult && fixResult.email === user.email && (
                                <Alert
                                  severity={fixResult.success ? "success" : "error"}
                                  sx={{ mt: 1 }}
                                >
                                  {fixResult.success
                                    ? `修复成功！已创建 ${fixResult.purchases_created} 条购买记录`
                                    : fixResult.message}
                                </Alert>
                              )}
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              展开查看详情后加载Stripe官方数据
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default UserManagementPage;
